import { useState } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Plus, Edit, X, Check } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const emptyForm = () => ({
  nome: "",
  prazopadrao: 7,
  prazougente: 2,
  ativo: true,
});

export default function ResolveConfig() {
  const { user } = useGoogleAuth();
  const isAdmin = user?.role === "admin";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm());

  const utils = trpc.useUtils();
  const { data: tipos = [], isLoading } = trpc.resolve.tipos.list.useQuery();

  const createMut = trpc.resolve.tipos.create.useMutation({
    onSuccess: () => {
      toast.success("Tipo criado com sucesso!");
      utils.resolve.tipos.list.invalidate();
      setIsModalOpen(false);
      setForm(emptyForm());
    },
    onError: (e) => toast.error(e.message),
  });

  const updateMut = trpc.resolve.tipos.update.useMutation({
    onSuccess: () => {
      toast.success("Tipo atualizado com sucesso!");
      utils.resolve.tipos.list.invalidate();
      setIsModalOpen(false);
      setEditingId(null);
      setForm(emptyForm());
    },
    onError: (e) => toast.error(e.message),
  });

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setIsModalOpen(true);
  };

  const handleOpenEdit = (tipo: any) => {
    setEditingId(tipo.id);
    setForm({
      nome: tipo.nome,
      prazopadrao: tipo.prazopadrao,
      prazougente: tipo.prazougente,
      ativo: tipo.ativo === 1 || tipo.ativo === true,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = () => {
    if (!form.nome.trim()) return toast.error("Nome é obrigatório");
    if (form.prazopadrao < 1) return toast.error("Prazo padrão deve ser maior que 0");
    if (form.prazougente < 1) return toast.error("Prazo urgente deve ser maior que 0");
    if (editingId) {
      updateMut.mutate({ id: editingId, ...form });
    } else {
      createMut.mutate(form);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Acesso restrito a administradores.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Config. Tipos Resolve</h1>
          <p className="text-sm text-muted-foreground">Cadastro de tipos de solicitação e prazos</p>
        </div>
        <Button onClick={handleOpenCreate} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="w-4 h-4 mr-2" /> Novo Tipo
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead className="text-center">Prazo Padrão (dias)</TableHead>
              <TableHead className="text-center">Prazo Urgente (dias)</TableHead>
              <TableHead className="text-center">Ativo</TableHead>
              <TableHead className="w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : (tipos as any[]).length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  Nenhum tipo cadastrado.
                </TableCell>
              </TableRow>
            ) : (
              (tipos as any[]).map((tipo: any) => (
                <TableRow key={tipo.id}>
                  <TableCell className="font-medium">{tipo.nome}</TableCell>
                  <TableCell className="text-center">{tipo.prazopadrao}</TableCell>
                  <TableCell className="text-center">{tipo.prazougente}</TableCell>
                  <TableCell className="text-center">
                    {tipo.ativo === 1 || tipo.ativo === true ? (
                      <Check className="w-4 h-4 text-green-500 mx-auto" />
                    ) : (
                      <X className="w-4 h-4 text-red-400 mx-auto" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => handleOpenEdit(tipo)}
                    >
                      <Edit className="w-3 h-3" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Modal criar/editar */}
      <Dialog open={isModalOpen} onOpenChange={(o) => { if (!o) { setIsModalOpen(false); setEditingId(null); setForm(emptyForm()); } }}>
        <DialogContent className="w-[50%] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Tipo" : "Novo Tipo"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <label className="text-sm font-medium">Nome *</label>
              <Input
                value={form.nome}
                onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                placeholder="Ex: Reclamação"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-sm font-medium">Prazo Padrão (dias) *</label>
                <Input
                  type="number"
                  min={1}
                  value={form.prazopadrao}
                  onChange={(e) => setForm((f) => ({ ...f, prazopadrao: parseInt(e.target.value) || 1 }))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Prazo Urgente (dias) *</label>
                <Input
                  type="number"
                  min={1}
                  value={form.prazougente}
                  onChange={(e) => setForm((f) => ({ ...f, prazougente: parseInt(e.target.value) || 1 }))}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="ativo"
                checked={form.ativo}
                onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))}
                className="h-4 w-4"
              />
              <label htmlFor="ativo" className="text-sm font-medium">Ativo</label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => { setIsModalOpen(false); setEditingId(null); setForm(emptyForm()); }}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={createMut.isPending || updateMut.isPending}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {editingId ? "Salvar" : "Criar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
